import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, MapPin, Building2, Tag, Briefcase, Clock, Loader2, X, Navigation } from "lucide-react";
import { detectPlace, savedPlace, nearJobsUrl } from "../lib/nearby";

const RECENT_KEY = "f2j_recent_searches";
const MAX_RECENT = 5;
const DEBOUNCE_MS = 180;

const ICONS = {
  title: Search,
  job: Briefcase,
  company: Building2,
  category: Tag,
  district: MapPin,
  concelho: MapPin,
  recent: Clock,
  near: Navigation,
};

const deaccent = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function readRecents() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.slice(0, MAX_RECENT) : [];
  } catch {
    return []; // localStorage indisponível (modo privado)
  }
}

function pushRecent(entry) {
  try {
    const list = readRecents().filter((r) => r.label !== entry.label);
    list.unshift(entry);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
  } catch {
    /* sem localStorage: histórico simplesmente não é guardado */
  }
}

// Distritos + concelhos carregados uma única vez e partilhados por todas as instâncias.
let placesPromise = null;
function loadPlaces() {
  if (!placesPromise) {
    placesPromise = api
      .get("/meta")
      .then((r) => {
        const districts = r.data?.districts || {};
        const out = [];
        Object.keys(districts).forEach((district) => {
          out.push({ label: district, sublabel: "Distrito", district });
          (districts[district] || []).forEach((concelho) =>
            out.push({ label: concelho, sublabel: district, district, concelho })
          );
        });
        return out;
      })
      .catch(() => []);
  }
  return placesPromise;
}

export const SearchBox = ({
  variant = "hero",
  initialQ = "",
  initialWhere = "",
  autoFocus = false,
  className = "",
  whatTestId,
  whereTestId,
  formTestId,
}) => {
  const hero = variant !== "compact";
  const isHome = variant === "hero";
  const testIds = {
    form: formTestId || (isHome ? "hero-search-form" : "jobs-search-form"),
    what: whatTestId || (isHome ? "hero-search-what" : "jobs-search-input"),
    where: whereTestId || (isHome ? "hero-search-where" : "jobs-search-where"),
  };
  const navigate = useNavigate();
  const location = useLocation();
  const wrap = useRef(null);

  const [q, setQ] = useState(initialQ);
  const [where, setWhere] = useState(initialWhere);
  const [place, setPlace] = useState(initialWhere ? { label: initialWhere, district: initialWhere } : null);
  const [suggestions, setSuggestions] = useState([]);
  const [recents, setRecents] = useState([]);
  const [places, setPlaces] = useState([]);
  const [openList, setOpenList] = useState(null); // "q" | "where" | null
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRecents(readRecents());
    loadPlaces().then(setPlaces);
  }, []);

  // Sugestões do servidor (cargos, vagas, empresas, categorias, locais)
  useEffect(() => {
    if (openList !== "q") return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/search/suggest", { params: { q, limit: 8 } });
        if (!cancelled) setSuggestions(data?.suggestions || []);
      } catch {
        if (!cancelled) setSuggestions([]); // sem sugestões: a pesquisa normal continua a funcionar
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, q ? DEBOUNCE_MS : 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q, openList]);

  // Fechar ao clicar fora
  useEffect(() => {
    const onDown = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpenList(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const placeMatches = useMemo(() => {
    const needle = deaccent(where);
    const base = needle
      ? places.filter((p) => deaccent(p.label).includes(needle))
      : places.filter((p) => p.sublabel === "Distrito");
    return base
      .sort((a, b) => {
        const an = deaccent(a.label).startsWith(needle) ? 0 : 1;
        const bn = deaccent(b.label).startsWith(needle) ? 0 : 1;
        return an - bn || a.label.localeCompare(b.label);
      })
      .slice(0, 8)
      .map((p) => ({
        type: p.concelho ? "concelho" : "district",
        label: p.label,
        sublabel: p.sublabel,
        place: p,
      }));
  }, [where, places]);

  const nearItem = useMemo(() => {
    const place = savedPlace();
    return {
      type: "near",
      label: place ? `Vagas perto de ${place.concelho}` : "Vagas perto de mim",
      sublabel: place ? "A tua localização guardada" : "Usa a tua localização",
      count: 0,
      near: true,
    };
  }, []);

  const recentItems = useMemo(
    () => (q ? [] : recents.map((r) => ({ ...r, type: "recent", sublabel: "Pesquisa recente" }))),
    [q, recents]
  );

  const list =
    openList === "where"
      ? placeMatches
      : q
      ? suggestions
      : [nearItem, ...recentItems, ...suggestions];

  const goToResults = useCallback(
    (filters = {}) => {
      // Se já estamos na lista de resultados, preservar os filtros ativos
      // (regime, modalidade, ordenação, destaque, distância, etc.).
      const onJobsList = location.pathname === "/empregos";
      const p = new URLSearchParams(onJobsList ? location.search : "");
      const text = filters.q !== undefined ? filters.q : q.trim();
      if (text) p.set("q", text);
      else p.delete("q");
      if (filters.category) p.set("category", filters.category);
      const district = filters.district || place?.district || (where.trim() ? where.trim() : "");
      const concelho = filters.concelho || (filters.district ? "" : place?.concelho || "");
      if (district) p.set("district", district);
      else p.delete("district");
      if (concelho) p.set("concelho", concelho);
      else p.delete("concelho");
      setOpenList(null);
      navigate(`/empregos?${p.toString()}`);
    },
    [navigate, q, where, place, location.pathname, location.search]
  );

  const goNearMe = useCallback(async () => {
    setOpenList(null);
    try {
      const place = savedPlace() || (await detectPlace());
      navigate(nearJobsUrl(place));
    } catch (e) {
      toast.error(e?.message || "Não conseguimos obter a tua localização.");
    }
  }, [navigate]);

  const choose = useCallback(
    (item) => {
      setActive(-1);
      if (item.near) return goNearMe();
      if (item.type === "recent") {
        pushRecent({ label: item.label, href: item.href, filters: item.filters });
        setRecents(readRecents());
        setOpenList(null);
        if (item.href && (item.href.startsWith("/empregos/") || item.href.startsWith("/empresas/")))
          return navigate(item.href);
        return goToResults(item.filters || { q: item.label });
      }
      if (item.place) {
        setPlace(item.place);
        setWhere(item.place.label);
        setOpenList(null);
        return undefined;
      }
      pushRecent({ label: item.label, href: item.href, filters: item.filters });
      setRecents(readRecents());
      setOpenList(null);
      if (item.type === "job" || item.type === "company") return navigate(item.href);
      return goToResults(item.filters || { q: item.label });
    },
    [goToResults, navigate, goNearMe]
  );

  const submit = (e) => {
    e.preventDefault();
    if (active >= 0 && list[active]) return choose(list[active]);
    return goToResults();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setOpenList(null);
      setActive(-1);
    }
  };

  const dropdown = openList && list.length > 0 && (
    <div
      data-testid="search-suggestions"
      role="listbox"
      className={`absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-black/5 shadow-2xl shadow-slate-300/50 overflow-hidden z-50 ${
        hero ? "" : "text-sm"
      }`}
    >
      {!q && openList === "q" && recentItems.length === 0 && suggestions.length > 0 && (
        <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-400">
          Pesquisas populares
        </p>
      )}
      {list.map((item, i) => {
        const Icon = ICONS[item.type] || Search;
        return (
          <button
            key={`${item.type}-${item.label}`}
            type="button"
            role="option"
            aria-selected={i === active}
            data-testid={`search-suggestion-${i}`}
            onMouseEnter={() => setActive(i)}
            onClick={() => choose(item)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
              i === active ? "bg-amber-50" : "hover:bg-slate-50"
            }`}
          >
            <Icon className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.75} />
            <span className="flex-1 min-w-0">
              <span className="block text-[15px] font-medium text-slate-900 truncate">{item.label}</span>
              {item.sublabel && (
                <span className="block text-xs text-slate-500 truncate">{item.sublabel}</span>
              )}
            </span>
            {item.count > 0 && (
              <span className="text-[11px] font-semibold text-slate-400 tabular-nums shrink-0">
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (!hero) {
    return (
      <div ref={wrap} className={`relative ${className}`}>
        <form onSubmit={submit} data-testid="nav-search-form">
          <div className="flex items-center h-10 rounded-full bg-slate-100/80 border border-transparent focus-within:bg-white focus-within:border-black/10 focus-within:shadow-md transition-all px-3.5">
            <Search className="w-4 h-4 text-slate-400 mr-2 shrink-0" />
            <input
              data-testid="nav-search-input"
              value={q}
              autoFocus={autoFocus}
              onChange={(e) => {
                setQ(e.target.value);
                setActive(-1);
                setOpenList("q");
              }}
              onFocus={() => setOpenList("q")}
              onKeyDown={onKeyDown}
              placeholder="Pesquisar vagas, empresas, cidades..."
              className="flex-1 bg-transparent border-0 outline-none text-sm text-slate-900 placeholder:text-slate-400"
            />
            {loading && <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin ml-2" />}
            {!loading && q && (
              <button type="button" aria-label="Limpar pesquisa" onClick={() => { setQ(""); setActive(-1); }}>
                <X className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700" />
              </button>
            )}
          </div>
        </form>
        {dropdown}
      </div>
    );
  }

  return (
    <div ref={wrap} className={`relative ${isHome ? "max-w-2xl" : ""} ${className}`}>
      <form
        onSubmit={submit}
        data-testid={testIds.form}
        className="flex flex-col sm:flex-row gap-2 bg-white rounded-3xl sm:rounded-full p-2.5 shadow-2xl shadow-slate-300/60 border border-black/5 focus-within:shadow-slate-400/50 transition-shadow"
      >
        <div className="flex items-center flex-1 px-4">
          <Search className="w-5 h-5 text-slate-400 mr-3 shrink-0" strokeWidth={2} />
          <input
            data-testid={testIds.what}
            value={q}
            autoFocus={autoFocus}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(-1);
              setOpenList("q");
            }}
            onFocus={() => setOpenList("q")}
            onKeyDown={onKeyDown}
            placeholder="Cargo, empresa ou palavra-chave"
            className="w-full h-12 bg-transparent border-0 outline-none text-[17px] text-slate-900 placeholder:text-slate-400"
          />
          {loading && <Loader2 className="w-4 h-4 text-slate-400 animate-spin ml-2 shrink-0" />}
        </div>

        <div className="flex items-center flex-1 px-4 sm:border-l border-black/5">
          <MapPin className="w-5 h-5 text-slate-400 mr-3 shrink-0" strokeWidth={2} />
          <input
            data-testid={testIds.where}
            value={where}
            onChange={(e) => {
              setWhere(e.target.value);
              setPlace(null);
              setActive(-1);
              setOpenList("where");
            }}
            onFocus={() => setOpenList("where")}
            onKeyDown={onKeyDown}
            placeholder="Distrito ou concelho"
            className="w-full h-12 bg-transparent border-0 outline-none text-[17px] text-slate-900 placeholder:text-slate-400"
          />
        </div>

        <button
          data-testid="hero-search-btn"
          type="submit"
          className="h-12 px-9 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[15px] font-semibold transition-colors shrink-0"
        >
          Procurar
        </button>
      </form>
      {dropdown}
    </div>
  );
};

export default SearchBox;
